import { useMutation, useQueryClient } from '@tanstack/react-query';

import { creditsKeys } from '#creditFeature/models/credits/keys';
import { prospectQueryKeys } from '#leadOverviewFeature/models/prospectQueries/keys';

import {
	confirmPlan,
	createDraft,
	discardDraft,
	saveRun,
	sendMessage,
	setPopupOpen,
	stopRun,
	updateMandate,
	updateRadius,
} from '../api/agentRunsApi';

import { agentRunKeys, agentRunMutationKeys } from './keys';

function useWriteRun() {
	const qc = useQueryClient();
	return (run) => {
		if (!run) return;
		qc.setQueryData(agentRunKeys.detail(run.id), run);
		if (!run.savedAt) qc.setQueryData(agentRunKeys.draft(), run);
	};
}

export function useCreateDraft() {
	const writeRun = useWriteRun();
	return useMutation({
		mutationKey: agentRunMutationKeys.createDraft,
		mutationFn: createDraft,
		onSuccess: ({ run }) => writeRun(run),
	});
}

export function useDiscardDraft() {
	const qc = useQueryClient();
	return useMutation({
		mutationKey: agentRunMutationKeys.discardDraft,
		mutationFn: discardDraft,
		onSuccess: () => {
			qc.setQueryData(agentRunKeys.draft(), null);
			qc.invalidateQueries({ queryKey: creditsKeys.wallet() });
			qc.invalidateQueries({ queryKey: prospectQueryKeys.all });
		},
	});
}

export function useSendMessage() {
	const qc = useQueryClient();
	return useMutation({
		mutationKey: agentRunMutationKeys.message,
		mutationFn: sendMessage,
		onSuccess: (_data, { runId }) => {
			qc.invalidateQueries({ queryKey: agentRunKeys.detail(runId), exact: true });
			qc.invalidateQueries({ queryKey: agentRunKeys.draft(), exact: true });
		},
	});
}

export function useUpdateMandate() {
	const qc = useQueryClient();
	const writeRun = useWriteRun();
	return useMutation({
		mutationKey: agentRunMutationKeys.mandate,
		mutationFn: updateMandate,
		onSuccess: ({ run, estimate }, { runId }) => {
			writeRun(run);
			if (estimate) qc.setQueryData(agentRunKeys.estimate(runId), estimate);
		},
	});
}

export function useUpdateRadius() {
	const writeRun = useWriteRun();
	return useMutation({
		mutationKey: agentRunMutationKeys.radius,
		mutationFn: updateRadius,
		onSuccess: ({ run }) => writeRun(run),
	});
}

export function useConfirmPlan() {
	const qc = useQueryClient();
	return useMutation({
		mutationKey: agentRunMutationKeys.confirm,
		mutationFn: confirmPlan,
		onSuccess: (data, { runId }) => {
			if (data?.run) qc.setQueryData(agentRunKeys.detail(runId), data.run);
			qc.invalidateQueries({ queryKey: agentRunKeys.detail(runId), exact: true });
			qc.invalidateQueries({ queryKey: agentRunKeys.draft(), exact: true });
		},
	});
}

export function useStopRun() {
	const qc = useQueryClient();
	return useMutation({
		mutationKey: agentRunMutationKeys.stop,
		mutationFn: stopRun,
		onSuccess: (data, runId) => {
			if (data?.run) qc.setQueryData(agentRunKeys.detail(runId), data.run);
			qc.invalidateQueries({ queryKey: agentRunKeys.detail(runId), exact: true });
			qc.invalidateQueries({ queryKey: agentRunKeys.draft(), exact: true });
			qc.invalidateQueries({ queryKey: creditsKeys.wallet() });
		},
	});
}

export function useSaveRun() {
	const qc = useQueryClient();
	return useMutation({
		mutationKey: agentRunMutationKeys.save,
		mutationFn: saveRun,
		onSuccess: (_data, { runId }) => {
			qc.setQueryData(agentRunKeys.draft(), null);
			qc.invalidateQueries({ queryKey: agentRunKeys.detail(runId), exact: true });
			qc.invalidateQueries({ queryKey: prospectQueryKeys.all });
		},
	});
}

export function useSetPopupOpen() {
	return useMutation({
		mutationKey: agentRunMutationKeys.popup,
		mutationFn: setPopupOpen,
		meta: { suppressGlobalError: true },
	});
}
